import { html } from "htm/preact";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { getClient } from "../client.js";
import { NotJoined } from "../components/NotJoined.js";
import { Icon } from "../components/Icon.js";
import { loadCourse } from "../courseData.js";
import { navigate } from "../router.js";
import { scoreTone } from "../util.js";
import { TERMS } from "../copy.js";
import { requestWakeLock, releaseWakeLock } from "../wakeLock.js";
import { holesInPlay } from "../../../shared/handicap.js";
import { prefersReducedMotion, useChanged, useCountUp, useFlipList, useRecentDelta } from "../animation.js";
import {
  computeLiveRoundResults,
  findLiveRound,
  isRoundFinal,
  type LiveRoundResult,
} from "../../../shared/leaderboard.js";
import { computeFinalStandings, type StandingEntry } from "../../../shared/standings.js";
import {
  buildScoreboard,
  scoreboardPlacements,
  standingFigure,
  type RoundScoreboard,
  type ScoreboardRow as Row,
} from "../../../shared/scoreboard.js";
import type { Course, Round, TeeSet, Tournament } from "../../../shared/types.js";

/** How long each panel holds before the next one comes up. Long enough to read a
 * board of eight players from across a room, short enough that nobody at the bar
 * has to wait out a panel they don't care about. */
const PANEL_MS = 15000;
/** How long the controls stay up after the last sign of a person at the screen. */
const CONTROLS_IDLE_MS = 3000;

type PanelId = "standings" | "scoreboard" | "live" | "rounds";

const PANEL_TITLE: Record<PanelId, string> = {
  standings: "Standings",
  scoreboard: "Out on the course",
  // Renamed when the scoreboard took the old title: that panel is now the summary of
  // how the round is going, and this one is where you go to see it hole by hole.
  live: "Hole by hole",
  rounds: "The card so far",
};

/**
 * Whether a panel's numbers are about one round or the whole game.
 *
 * Stated on every panel rather than only where it might be unclear. On a screen that
 * changes itself every fifteen seconds, someone who looks up halfway through has no
 * idea how long the thing in front of them has been there — so "+13" needs to say what
 * it is counting without anyone having waited for the panel to arrive.
 */
const PANEL_SCOPE: Record<PanelId, string> = {
  standings: `Whole ${TERMS.event}`,
  scoreboard: "This round",
  live: "This round",
  rounds: `Whole ${TERMS.event}`,
};

/**
 * The whole screen's sizing, in one number.
 *
 * Every length in the clubhouse stylesheet is a multiple of `--club-scale`, so this
 * is the only thing that has to know how big the screen is. Width sets the ceiling —
 * a phone held sideways is not a television — and height divided by the number of
 * rows sets the floor, because the failure that actually matters is a board of twelve
 * players scrolling off the bottom where nobody standing up can see it.
 */
function clubScale(rows: number): number {
  if (typeof window === "undefined") return 1;
  // The divisors are the width and height the layout wants at scale 1. Height is the
  // binding one on most screens, and deliberately so: the first tuning of this was
  // width-led and left a four-player board floating in the middle of a 1440 display
  // at the size it would have been on a laptop, which is the one thing a screen
  // across a room must not do.
  // 620 is the app's own content width — the widest panel that has to fit *without*
  // scrolling. The eighteen-hole grid is wider than that and gets a scrolling fallback
  // on narrow screens instead, so sizing the whole screen around it only made phones
  // small for no one's benefit.
  const byWidth = window.innerWidth / 620;
  const byHeight = window.innerHeight / (rows * 86 + 300);
  return Math.min(Math.max(Math.min(byWidth, byHeight), 0.5), 2.4);
}

/** The tee set a round is played off, which is where its pars live. */
function teeSetFor(round: Round | null, courses: Map<string, Course>): TeeSet | null {
  if (!round) return null;
  const course = courses.get(round.courseId);
  return course?.teeSets.find((t) => t.name === round.teeSet) ?? null;
}

/** The hole numbers a round actually plays, in order. Via holesInPlay rather than a
 * filter of its own: it is the one place that knows a nine-hole card has no back nine
 * to narrow to, and this board is read from across a room — a panel that quietly showed
 * no holes would look like a scoring fault. */
function holesOf(round: Round, teeSet: TeeSet): number[] {
  return holesInPlay(teeSet.holes, round.holeSet).map((h) => h.number);
}

// ---------- panels ----------

/**
 * One player's line on the wall.
 *
 * Its own component for the same reason `StandingRow` is on the leaderboard: the
 * motion is per-row state, and hooks can't live inside a `.map()`.
 */
function ClubRow({
  entry,
  index,
  name,
  projected,
  ranked,
  live,
  register,
}: {
  entry: StandingEntry;
  index: number;
  name: string;
  projected: number;
  ranked: boolean;
  live: boolean;
  register: (key: string) => (el: HTMLElement | null) => void;
}) {
  const points = useCountUp(entry.totalPoints);
  // Negative because a smaller placement number is a better position — the cue should
  // point the way the player moved, not the way the integer did.
  const moved = useRecentDelta(-entry.placement, { enabled: live, delay: 700, hold: 3200 });
  const struck = useChanged(entry.totalPoints, 2600);
  const stagger = useRef(index);

  return html`
    <div
      class=${`club-row ${ranked && entry.placement === 1 ? "leader" : ""} ${
        moved > 0 ? "moved-up" : moved < 0 ? "moved-down" : ""
      } ${struck ? "struck" : ""}`}
      ref=${register(entry.playerId)}
      style=${`--i:${stagger.current}`}
    >
      <span class="club-pos">${ranked ? entry.placement : "–"}</span>
      <span class="club-name">${name}</span>
      ${moved !== 0 &&
      html`<span class="club-move">
        <${Icon} name=${moved > 0 ? "arrow-up" : "arrow-down"} size=${28} />
        ${Math.abs(moved)}
      </span>`}
      <span class="club-points">
        ${points}
        ${projected > 0 && html`<small class="club-projected">+${projected}</small>`}
      </span>
    </div>
  `;
}

function StandingsPanel({
  standings,
  nameOf,
  projected,
  ranked,
  live,
}: {
  standings: StandingEntry[];
  nameOf: (id: string) => string;
  projected: (id: string) => number;
  ranked: boolean;
  live: boolean;
}) {
  const register = useFlipList(standings.map((s) => s.playerId).join(","));

  return html`
    <div class="club-board">
      ${standings.map(
        (s, i) => html`
          <${ClubRow}
            key=${s.playerId}
            entry=${s}
            index=${i}
            name=${nameOf(s.playerId)}
            projected=${projected(s.playerId)}
            ranked=${ranked}
            live=${live}
            register=${register}
          />
        `,
      )}
    </div>
  `;
}

/** "E", "+4", "−4" — the scoreboard's own shorthand, where level is a letter so it
 * doesn't read as a score of zero. A typographic minus rather than a hyphen, which at
 * this size is a visible difference. */
function toParLabel(toPar: number | null): string {
  if (toPar == null) return "–";
  if (toPar === 0) return "E";
  return toPar > 0 ? `+${toPar}` : `−${Math.abs(toPar)}`;
}

/** One line of the scoreboard. Separate component because the motion is per-row
 * state, exactly as for `ClubRow`. */
function ScoreLine({
  row,
  placement,
  index,
  basis,
  register,
}: {
  row: Row;
  placement: number | null;
  index: number;
  basis: "net" | "gross";
  register: (key: string) => (el: HTMLElement | null) => void;
}) {
  const gross = useCountUp(row.gross ?? 0);
  const net = useCountUp(row.net ?? 0);
  const struck = useChanged(`${row.thru}:${row.gross}`, 2600);
  const stagger = useRef(index);
  // The to-par figure the round is decided on, so the big number on the row is the one
  // the position beside it was worked out from.
  const standing = standingFigure(row, basis);

  return html`
    <div
      class=${`club-score-row ${placement === 1 ? "leader" : ""} ${struck ? "struck" : ""} ${
        basis === "net" ? "has-net" : ""
      }`}
      ref=${register(row.id)}
      style=${`--i:${stagger.current}`}
    >
      <span class="club-pos">${placement ?? "–"}</span>
      <span class="club-name">
        ${row.label}
        ${row.handicap != null && html`<small class="club-hcp">${row.handicap}</small>`}
      </span>
      <span
        class=${`club-topar ${
          standing == null ? "" : standing < 0 ? "under" : standing > 0 ? "over" : "level"
        }`}
      >
        ${toParLabel(standing)}
      </span>
      <span class="club-thru">
        ${row.thru === 0 ? "–" : row.finished ? "F" : `thru ${row.thru}`}
      </span>
      <span class="club-gross">${row.gross == null ? "–" : gross}</span>
      ${basis === "net" && html`<span class="club-net">${row.net == null ? "–" : net}</span>`}
    </div>
  `;
}

/**
 * How the round is actually going, in strokes.
 *
 * The standings panel next door is the tournament's points, which mid-round says
 * almost nothing about the golf — everyone sits on the same figure until a round is
 * signed off. Strokes against par is the one reading that works the same way in every
 * format this app scores, which is why this panel doesn't consult the format at all
 * beyond asking whether a row is a player or a side.
 */
function ScoreboardPanel({ board }: { board: RoundScoreboard }) {
  const register = useFlipList(board.rows.map((r) => r.id).join(","));
  const placements = scoreboardPlacements(board.rows, board.basis);
  const net = board.basis === "net";

  return html`
    <div class="club-board">
      <div class=${`club-score-head ${net ? "has-net" : ""}`}>
        <span class="club-pos">Pos</span>
        <span class="club-name">${board.bySide ? "Side" : "Player"} ${net ? "· hcp" : ""}</span>
        <!-- Named for the basis rather than left as a bare "to par". A round played off
             handicap and one played straight up produce the same-looking number from
             different arithmetic, and the screen is the only thing that can say which. -->
        <span class="club-topar">${net ? "Net to par" : "To par"}</span>
        <span class="club-thru">Thru</span>
        <span class="club-gross">Gross</span>
        ${net && html`<span class="club-net">Net</span>`}
      </div>
      ${board.rows.map(
        (row, i) => html`
          <${ScoreLine}
            key=${row.id}
            row=${row}
            placement=${placements[i] ?? null}
            index=${i}
            basis=${board.basis}
            register=${register}
          />
        `,
      )}
    </div>
  `;
}

/**
 * The round being played, hole by hole, whole field.
 *
 * Gross strokes rather than the format's own points: this panel answers "how is it
 * going out there", and every format in the app is built on strokes, so a grid of
 * them reads the same way whatever is being played. The points panel is the one
 * next door.
 */
function LiveRoundPanel({
  round,
  teeSet,
  tournament,
  scores,
  board,
}: {
  round: Round;
  teeSet: TeeSet;
  tournament: Tournament;
  scores: { roundId: string; playerId: string; hole: number; strokes: number | null; updatedAt: number }[];
  /** For the three totals on the right. Taken from the scoreboard rather than summed
   * again here, so the grid and the panel next door can't disagree about a card. */
  board: RoundScoreboard | null;
}) {
  const holes = holesOf(round, teeSet);
  const parOf = new Map(teeSet.holes.map((h) => [h.number, h.par]));
  const mine = scores.filter((s) => s.roundId === round.id && s.strokes != null);
  const byKey = new Map(mine.map((s) => [`${s.playerId}:${s.hole}`, s.strokes as number]));

  // The hole that just changed, so it can flash. One hook for the whole grid rather
  // than one per cell — a `useChanged` inside the cell map would add and drop hooks
  // as the field changes shape.
  const newest = mine.reduce<(typeof mine)[number] | null>(
    (best, s) => (!best || s.updatedAt > best.updatedAt ? s : best),
    null,
  );
  const newestKey = newest ? `${newest.playerId}:${newest.hole}` : "";
  const flashing = useChanged(newestKey, 2600);

  // Only worth three extra columns when the handicap actually changes the answer.
  const showNet = board?.basis === "net";
  const rowFor = new Map((board?.rows ?? []).map((r) => [r.id, r]));

  return html`
    <div class="club-grid-wrap">
      <table class="club-grid">
        <thead>
          <tr>
            <th class="club-grid-name">Hole</th>
            ${holes.map((h) => html`<th key=${h}>${h}</th>`)}
            <th class="club-grid-total">Gross</th>
            ${showNet &&
            html`
              <th class="club-grid-total">Hcp</th>
              <th class="club-grid-total">Net</th>
            `}
          </tr>
          <tr class="club-grid-par">
            <th>Par</th>
            ${holes.map((h) => html`<th key=${h}>${parOf.get(h) ?? "–"}</th>`)}
            <th>${holes.reduce((sum, h) => sum + (parOf.get(h) ?? 0), 0)}</th>
            ${showNet &&
            html`
              <th></th>
              <th></th>
            `}
          </tr>
        </thead>
        <tbody>
          ${tournament.players.map((p) => {
            const played = holes.filter((h) => byKey.has(`${p.id}:${h}`));
            const total = played.reduce((sum, h) => sum + (byKey.get(`${p.id}:${h}`) ?? 0), 0);
            const figures = rowFor.get(p.id) ?? null;
            return html`
              <tr key=${p.id}>
                <td class="club-grid-name">${p.name}</td>
                ${holes.map((h) => {
                  const key = `${p.id}:${h}`;
                  const strokes = byKey.get(key) ?? null;
                  const par = parOf.get(h) ?? 0;
                  return html`<td
                    key=${h}
                    class=${`${scoreTone(strokes, par)} ${flashing && key === newestKey ? "struck" : ""}`}
                  >
                    ${strokes ?? "·"}
                  </td>`;
                })}
                <td class="club-grid-total">${played.length > 0 ? total : "–"}</td>
                ${showNet &&
                html`
                  <td class="club-grid-total club-grid-hcp">
                    ${figures && figures.thru > 0 ? `−${figures.strokesReceived}` : "–"}
                  </td>
                  <td class="club-grid-total">${figures?.net ?? "–"}</td>
                `}
              </tr>
            `;
          })}
        </tbody>
      </table>
    </div>
  `;
}

/** Every round, where it stands, and who took the ones that are done. */
function RoundsPanel({
  tournament,
  results,
  nameOf,
}: {
  tournament: Tournament;
  results: LiveRoundResult[];
  nameOf: (id: string) => string;
}) {
  return html`
    <div class="club-rounds">
      ${tournament.rounds.map((round) => {
        const result = results.find((r) => r.roundId === round.id);
        const points = Object.entries(result?.points ?? {});
        const best = points.reduce((top, [, v]) => Math.max(top, v), -Infinity);
        // A round nobody has scored pays everyone nothing, and "everyone leads on 0"
        // is not a result worth putting on a wall.
        const leaders =
          best > 0 ? points.filter(([, v]) => v === best).map(([id]) => nameOf(id)) : [];
        const settled = isRoundFinal(round, tournament);

        return html`
          <div key=${round.id} class=${`club-round ${settled ? "is-done" : ""}`}>
            <span class="club-round-name">${round.name}</span>
            <span class="club-round-state">
              ${!result || result.holesTotal === 0
                ? "waiting on a course"
                : settled
                  ? "final"
                  : result.holesScored === 0
                    ? "not started"
                    : `${result.holesScored} of ${result.holesTotal} holes`}
            </span>
            <span class="club-round-leader">
              ${leaders.length > 0 ? leaders.join(" & ") : "—"}
              ${leaders.length > 0 && html`<small> ${best} pts</small>`}
            </span>
          </div>
        `;
      })}
    </div>
  `;
}

// ---------- the screen ----------

export function Clubhouse({ code }: { code: string }) {
  const client = getClient(code);
  const snapshot = client?.snapshot.value ?? null;
  const hydrated = client?.hydrated.value ?? false;
  const [courses, setCourses] = useState<Map<string, Course>>(new Map());
  const [step, setStep] = useState(0);
  const [paused, setPaused] = useState(false);
  const [controlsUp, setControlsUp] = useState(true);
  const [scale, setScale] = useState(1);

  const tournament = snapshot?.tournament ?? null;
  const courseIds = useMemo(
    () => [...new Set((tournament?.rounds ?? []).map((r) => r.courseId).filter(Boolean))],
    [tournament?.rounds],
  );

  useEffect(() => {
    if (courseIds.length === 0) return;
    Promise.all(courseIds.map((id) => loadCourse(id))).then((loaded) => {
      const map = new Map<string, Course>();
      loaded.forEach((c) => c && map.set(c.id, c));
      setCourses(map);
    });
  }, [courseIds.join(",")]);

  // This screen exists to be left running, so the one thing it must not do is let the
  // display sleep halfway through a round.
  useEffect(() => {
    requestWakeLock();
    return () => releaseWakeLock();
  }, []);

  /*
   * `#app` is capped at 620px, which is right for every other screen in a phone-first
   * app and exactly wrong for this one — a television rendered the board in a narrow
   * strip down the middle with the scorecard's eighteen columns piled on top of each
   * other inside it. The cap has to come off from outside the component, since the
   * element holding it is the render root.
   *
   * A class on <html> rather than `#app:has(.clubhouse)` because the theme already
   * works this way, and it releases cleanly on unmount rather than depending on what
   * happens to be rendered.
   */
  useEffect(() => {
    document.documentElement.classList.add("clubhouse-mode");
    return () => document.documentElement.classList.remove("clubhouse-mode");
  }, []);

  const rowCount = Math.max(tournament?.players.length ?? 0, 4);
  useEffect(() => {
    const fit = () => setScale(clubScale(rowCount));
    fit();
    window.addEventListener("resize", fit);
    window.addEventListener("orientationchange", fit);
    return () => {
      window.removeEventListener("resize", fit);
      window.removeEventListener("orientationchange", fit);
    };
  }, [rowCount]);

  // The controls have no app bar to live in, so they surface on any sign of a person
  // and then get out of the way again. Touch counts: on a tablet propped at the bar
  // there is no pointer to move.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const wake = () => {
      setControlsUp(true);
      clearTimeout(timer);
      timer = setTimeout(() => setControlsUp(false), CONTROLS_IDLE_MS);
    };
    wake();
    for (const event of ["pointermove", "pointerdown", "keydown", "touchstart"]) {
      window.addEventListener(event, wake);
    }
    return () => {
      clearTimeout(timer);
      for (const event of ["pointermove", "pointerdown", "keydown", "touchstart"]) {
        window.removeEventListener(event, wake);
      }
    };
  }, []);

  const roundResults = useMemo(
    () =>
      tournament
        ? computeLiveRoundResults(tournament, courses, snapshot!.scores, snapshot!.perHoleData)
        : [],
    [tournament, courses, snapshot?.scores, snapshot?.perHoleData],
  );

  const liveRound = tournament ? findLiveRound(tournament, roundResults) : null;
  const liveTeeSet = teeSetFor(liveRound, courses);
  const liveCourse = liveRound ? (courses.get(liveRound.courseId) ?? null) : null;

  const board =
    liveRound && liveCourse && tournament && snapshot
      ? buildScoreboard(liveRound, liveCourse, tournament.players, snapshot.scores)
      : null;

  // Panels with nothing behind them are dropped rather than shown empty: a game with
  // nobody out on the course would otherwise spend a third of every cycle on a grid
  // of dots.
  const panels: PanelId[] = ["standings"];
  // Summary before detail — the scoreboard says how it's going, the grid says how it
  // got there.
  if (board) panels.push("scoreboard");
  if (liveRound && liveTeeSet) panels.push("live");
  if ((tournament?.rounds.length ?? 0) > 0) panels.push("rounds");

  // Modulo rather than a clamped index: the list shrinks when a round closes, and a
  // step that pointed past the end would otherwise land on nothing.
  const active = panels[step % panels.length] ?? "standings";

  const rotating = panels.length > 1 && !paused && !prefersReducedMotion();
  useEffect(() => {
    if (!rotating) return;
    const timer = setTimeout(() => setStep((s) => s + 1), PANEL_MS);
    return () => clearTimeout(timer);
  }, [rotating, step, active]);

  if (!client) return html`<${NotJoined} code=${code} />`;
  if (!snapshot || !tournament) {
    return html`<div class="clubhouse is-loading"><p>Warming up the screen…</p></div>`;
  }

  const settled = roundResults.filter((r) => !r.provisional);
  const inPlay = roundResults.filter((r) => r.provisional);
  const standings = computeFinalStandings(
    tournament.players.map((p) => p.id),
    settled,
    tournament.finalTiebreaker,
  );
  const nameOf = (id: string) => tournament.players.find((p) => p.id === id)?.name ?? id;
  const projected = (id: string) => inPlay.reduce((sum, r) => sum + (r.points[id] ?? 0), 0);
  const ranked = new Set(standings.map((s) => s.placement)).size > 1;
  // Movement cues wait on complete inputs, exactly as the leaderboard's do: rounds
  // can't score before their course loads, so a board that is still filling in would
  // otherwise report everybody overtaking everybody.
  const inputsSettled = hydrated && courseIds.every((id) => courses.has(id));

  // Which round is out there belongs in the header rather than in the grid's corner
  // cell, where a fixed-width column truncated it to "Saturday Fou…".
  const onRound = active === "live" || active === "scoreboard";
  const eyebrow =
    onRound && liveRound ? `${PANEL_TITLE[active]} · ${liveRound.name}` : PANEL_TITLE[active];

  /*
   * Where the group is, as one fact about the field rather than a column on every row.
   *
   * "Through" is the hole everybody has finished — the last one where the whole card
   * is in — and "playing" is the one they're on now. They differ by more than one
   * whenever somebody is a hole or two behind, which is most of an actual round, and
   * showing only the first of them made the screen look stuck.
   */
  const progress =
    onRound && board
      ? [
          board.fieldThru > 0 ? `Thru ${board.fieldThru}` : "Just away",
          board.currentHole != null ? `playing ${board.currentHole}` : "all in",
        ].join(" · ")
      : null;

  const go = (delta: number) => {
    setStep((s) => (s + delta + panels.length) % panels.length);
  };

  return html`
    <div class="clubhouse" style=${`--club-scale:${scale}`}>
      <header class="club-head">
        <div>
          <span class="club-eyebrow">
            <span class="club-scope">${PANEL_SCOPE[active]}</span>
            ${eyebrow}
          </span>
          <h1 class="club-title">${tournament.name}</h1>
        </div>
        <span class="club-head-right">
          ${progress && html`<span class="club-progress-read">${progress}</span>`}
          <span class="club-code">${code}</span>
        </span>
      </header>

      <div class="club-stage" key=${active}>
        ${active === "standings" &&
        html`<${StandingsPanel}
          standings=${standings}
          nameOf=${nameOf}
          projected=${projected}
          ranked=${ranked}
          live=${inputsSettled}
        />`}
        ${active === "scoreboard" && board && html`<${ScoreboardPanel} board=${board} />`}
        ${active === "live" &&
        liveRound &&
        liveTeeSet &&
        html`<${LiveRoundPanel}
          round=${liveRound}
          teeSet=${liveTeeSet}
          tournament=${tournament}
          scores=${snapshot.scores}
          board=${board}
        />`}
        ${active === "rounds" &&
        html`<${RoundsPanel} tournament=${tournament} results=${roundResults} nameOf=${nameOf} />`}
      </div>

      ${panels.length > 1 &&
      html`<div class="club-progress">
        <div
          class=${`club-progress-fill ${rotating ? "is-running" : ""}`}
          key=${step}
          style=${`animation-duration:${PANEL_MS}ms`}
        ></div>
      </div>`}

      <div class=${`club-controls ${controlsUp ? "is-up" : ""}`}>
        <button
          class="icon-btn"
          onClick=${() => navigate(`/t/${code}/leaderboard`)}
          aria-label="Leave the clubhouse screen"
          title="Leave the clubhouse screen"
        >
          <${Icon} name="close" />
        </button>
        ${panels.length > 1 &&
        html`
          <button class="icon-btn" onClick=${() => go(-1)} aria-label="Previous panel">
            <${Icon} name="left" />
          </button>
          <button
            class="icon-btn"
            onClick=${() => setPaused((p) => !p)}
            aria-label=${paused ? "Resume rotating" : "Hold this panel"}
            title=${paused ? "Resume rotating" : "Hold this panel"}
          >
            <${Icon} name=${paused ? "play" : "pause"} />
          </button>
          <button class="icon-btn" onClick=${() => go(1)} aria-label="Next panel">
            <${Icon} name="right" />
          </button>
        `}
        <span class="club-controls-hint">${`${TERMS.Event} code ${code}`}</span>
      </div>
    </div>
  `;
}
